import {
    RiArrowUpDoubleFill,
    RiDeleteBinLine,
    RiEdit2Line,
    RiEqualizerLine,
    RiFileCopyLine,
    RiKanbanView,
    RiListUnordered,
} from "@remixicon/react";
import React, { useCallback, useState } from "react";
import Button from "../../theme/button/Button";
import TabList, { MenuItemOrDivider, TabItem } from "../../theme/tab-list/TabList";

import {
    Dashboard, DashboardGroupBy,
    DashboardInput,
    DashboardSettingsInput,
    DashboardSortBy, DashboardView, GetUserDashboardsDocument,
    PermissionAction,
    UserPortalSettingsDashboard,
    useCreateDashboardMutation,
    useDeleteDashboardMutation,
    useSaveUserLastOpenedDashboardIdMutation,
    useSaveUserPortalSettingsMutation,
} from "../../../__generated__/api";
import { cloneWithoutTypename } from "../../../api";
import useLogging, { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import { useTicketState } from "../../../contexts/ticket-state/TicketState";
import Confirm from "../../confirm/Confirm";
import { LoadingIcon } from "../../loading/Loading";
import FormGroup from "../../theme/form-group/FormGroup";
import Styles from "./BoardToolbar.module.scss";
import Filters from "./Filters";
import GroupByFilter from "./GroupBy";
import SortByFilter from "./SortBy";
import { CreateEditView, ViewOptionsFilter } from "./view-options/ViewOptions";

function createMenuItem(dashboard: Partial<UserPortalSettingsDashboard>): TabItem {
    const isKanban = dashboard.dashboard?.settings?.view === DashboardView.Kanban;

    return {
        id: dashboard.dashboard?.id,
        title: dashboard.dashboard?.name ?? (isKanban ? "Board" : "List"),
        icon: isKanban ? <RiKanbanView /> : <RiListUnordered />,
        isPinned: dashboard.isPinned,
        canShowMenu: dashboard.permission != null && dashboard.permission !== PermissionAction.Read,
    };
}
export default function BoardToolbar() {
    const ticketState = useTicketState();
    const [createDashboard] = useCreateDashboardMutation();
    const [deleteDashboard] = useDeleteDashboardMutation();
    const [saveUserSettings] = useSaveUserPortalSettingsMutation();
    const [saveLastOpened] = useSaveUserLastOpenedDashboardIdMutation();

    const value = ticketState.currentDashboardSettings;
    const [shouldEditView, setShouldEditView] = useState(false);
    const [editingView, setEditingView] = useState<Dashboard>();

    const dashboardList = ticketState.userSettings?.dashboards;

    const [deleting, setDeleting] = useState<Dashboard>();
    const [renaming, setRenaming] = useState<DashboardInput & {id: string}>();

    const logging = useLogging();

    const searchState = ticketState.searchState.useSearchContext();

    const permission = dashboardList?.find(dash => dash.dashboard.id === ticketState.currentDashboard.id)?.permission;
    const save = useCallback(async (dashboard: DashboardInput, id?: string) => {
        try {
            if (id) {
                ticketState.saveDashboard(dashboard, id);
            } else {
                const { data } = await createDashboard({
                    refetchQueries: [GetUserDashboardsDocument],
                    variables: {
                        dashboard,
                    },
                });
                ticketState.userSettings = {
                    ...ticketState.userSettings,
                    dashboards: [
                        ...(ticketState.userSettings?.dashboards ?? []),
                        {
                            dashboard: data?.createDashboard as Dashboard,
                            dashboardId: data?.createDashboard.id,
                            permission: PermissionAction.Modify,
                            isPinned: false,
                        },
                    ],
                };
                ticketState.currentDashboard = data?.createDashboard as Dashboard;
            }
        } catch (err) {
            logging.error({ err, id }, "Failed to save dashboard");
        }
    }, [ticketState]);

    if (!ticketState?.currentDashboardSettings) {
        return <LoadingIcon />;
    }

    const setDashboardSettings = async (settings: DashboardSettingsInput) => {
        ticketState.currentDashboardSettings = settings;
    };

    const doDelete = async (id: string) => {
        try {
            const filtered = dashboardList?.filter(item => item.dashboard.id !== id);
            ticketState.userSettings = {
                ...ticketState.userSettings,
                dashboards: filtered,
            };
            const first = ticketState.userSettings?.dashboards?.find(dash => dash.dashboardId !== id);
            ticketState.currentDashboard = first?.dashboard as Dashboard;

            await deleteDashboard({
                refetchQueries: [GetUserDashboardsDocument],
                variables: {
                    id,
                },
            });
        } catch (err) {
            logging.error({ err }, "Failed to delete dashboard");
        }
    };
    const canDelete = (dashboardList?.length ?? 0) > 1;
    return (
        <div data-tour-panel="board-top" className={`w-100 d-flex p3x-4 border-bottom align-items-center ${Styles.panel}`}>
            <div className="flex-grow-1 border-0 align-items-bottom mt-auto">
                <Confirm
                    title="Delete View?"
                    shouldShow={deleting != null}
                    confirmText="Delete"
                    closeText="Cancel"
                    closeVariant="outline-gray"
                    confirmVariant="danger"
                    dialogClassName="centered-container-md"
                    onHide={() => setDeleting(undefined)}
                    onConfirm={() => {
                        doDelete(deleting?.id);
                        setDeleting(undefined);
                    }}
                >
                    <>
                        Are you sure you want to delete the &quot;
                        {deleting?.name as string}
                        &quot; view?
                    </>
                </Confirm>
                <Confirm
                    title="View Name"
                    shouldShow={renaming != null}
                    confirmText="Save"
                    closeText="Cancel"
                    closeVariant="outline-gray"
                    confirmVariant="primary"
                    dialogClassName="centered-container-md"
                    onHide={() => setRenaming(undefined)}
                    onConfirm={() => {
                        if (renaming) {
                            save(renaming, renaming.id);
                        }
                        setRenaming(undefined);
                    }}
                >
                    <FormGroup
                        type="text"
                        label="Rename View"
                        autoFocus={renaming != null}
                        variant="default"
                        value={renaming?.name ?? ""}
                        onChange={event => setRenaming({
                            ...renaming,
                            id: renaming?.id as string,
                            name: event.target.value,
                        })}
                    />
                </Confirm>
                <CreateEditView
                    shouldShow={shouldEditView}
                    onHide={() => setShouldEditView(false)}
                    isEditing={editingView != null}
                    key={editingView?.id}
                    onChange={dashboard => {
                        if (!dashboard) {
                            return;
                        }
                        save(dashboard, editingView?.id);
                        setShouldEditView(false);
                        setEditingView(undefined);
                    }}
                    value={editingView}
                />
                <TabList
                    className="border-0 "
                    onAdd={() => {
                        setShouldEditView(true);
                        setEditingView(undefined);
                        LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "tabs", "add"]);
                    }}
                    onReorder={items => {
                        if (ticketState.searchState.isSearching) {
                            return;
                        }
                        const dashboardMap = dashboardList?.toIdMap(row => row.dashboard.id);
                        const filtered = items?.map(item => dashboardMap?.[item.id.toString()]).filterNull();
                        ticketState.userSettings = {
                            ...ticketState.userSettings,
                            dashboards: filtered,
                        };
                        saveUserSettings({
                            variables: {
                                settings: {
                                    dashboards: items.map(row => ({
                                        dashboardId: row.id,
                                        isPinned: row.isPinned ?? false,
                                    })),
                                },
                            },
                        });
                        LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "tabs", "reorder"]);
                    }}
                    maxCount={5}
                    selectedId={ticketState.currentDashboard?.id}
                    items={dashboardList?.map(createMenuItem) ?? []}
                    onSelected={async selectedId => {
                        ticketState.searchState.hideSearch();
                        const dashboard = ticketState.userSettings?.dashboards?.find(
                            current => current.dashboard.id === selectedId,
                        )?.dashboard;
                        if (dashboard) {
                            ticketState.currentDashboard = dashboard as Dashboard;

                            try {
                                await saveLastOpened({
                                    variables: {
                                        settings: {
                                            lastOpenedDashboardId: dashboard.id,
                                        },
                                    },
                                });
                                LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "tabs", "click"]);
                            } catch (err) {
                                logging.error({ err }, "Failed to save last opened dashboard");
                            }
                        }
                    }}
                    menuItems={ticketState.searchState.isSearching ? undefined : [
                        {
                            label: "Rename",
                            icon: <RiEdit2Line />,
                            onClick: item => {
                                const current = ticketState.userSettings?.dashboards?.find(
                                    board => board.dashboard.id === item.id,
                                );
                                if (!current) {
                                    return;
                                }
                                setRenaming(current.dashboard as Dashboard);
                            },
                        } as MenuItemOrDivider,
                        // {
                        //     label: "Pin",
                        //     icon: <RiPushpinLine />,
                        //     onClick: item => {
                        //         // rename
                        //     },
                        // },
                        {
                            label: "Edit",
                            icon: <RiEqualizerLine />,
                            onClick: item => {
                                const current = ticketState.userSettings?.dashboards?.find(
                                    board => board.dashboard.id === item.id,
                                );
                                if (!current) {
                                    return;
                                }
                                setEditingView(current.dashboard as Dashboard);
                                setShouldEditView(true);
                            },
                        } as MenuItemOrDivider,
                        // {
                        //     label: "Copy Link",
                        //     icon: <RiLinksLine />,
                        //     onClick: item => {
                        //         // rename
                        //     },
                        // },
                        {
                            label: "Duplicate",
                            icon: <RiFileCopyLine />,
                            onClick: item => {
                                const current = ticketState.userSettings?.dashboards?.find(
                                    board => board.dashboard.id === item.id,
                                );
                                if (!current) {
                                    return;
                                }
                                save({
                                    ...cloneWithoutTypename(current.dashboard as Dashboard),
                                    id: undefined,
                                    permissions: [],
                                } as Dashboard);
                            },
                        } as MenuItemOrDivider,
                        canDelete ? {
                            isDivider: true,
                        } as MenuItemOrDivider : undefined,
                        // {
                        //     label: "Reset view to defaults",
                        //     icon: <RiRefreshLine />,
                        //     onClick: item => {
                        //         // rename
                        //     },
                        // },
                        canDelete
                            ? {
                                label: "Delete",
                                icon: <RiDeleteBinLine />,
                                onClick: item => {
                                    const current = ticketState.userSettings?.dashboards?.find(
                                        board => board.dashboard.id === item.id,
                                    );
                                    if (!current) {
                                        return;
                                    }
                                    setDeleting(current.dashboard as Dashboard);
                                },
                            } as MenuItemOrDivider : undefined,
                    ].filterNull()}
                />
            </div>
            {permission && permission !== PermissionAction.Read && (
                <div data-tour-panel="filters" className="d-flex gap-2 flex-shrink-0">
                    <Filters
                        onChange={filters => {
                            setDashboardSettings({
                                ...value,
                                filters,
                            });
                            LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "controls", "filters"]);
                        }}
                        value={value?.filters}
                    />
                    {!searchState.isSearching && (
                        <SortByFilter
                            onChange={sortBy => {
                                LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "controls", "sort"]);
                                setDashboardSettings({
                                    ...value,
                                    sortBy,
                                });
                            }}
                            value={value?.sortBy ?? DashboardSortBy.Manual}
                        />
                    )}
                    <GroupByFilter
                        onChange={groupBy => {
                            LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "controls", "group"]);
                            setDashboardSettings({
                                ...value,
                                groupBy,
                            });
                        }}
                        value={value?.groupBy ?? DashboardGroupBy.NoGroup}
                    />
                    {!searchState.isSearching && (
                        <ViewOptionsFilter
                            onChange={settings => {
                                save({
                                    ...ticketState.currentDashboard,
                                    ...settings,
                                } as Dashboard, ticketState.currentDashboard.id);
                            }}
                            value={ticketState.currentDashboard}
                        />
                    )}
                    {/* TODO: Find a proper way to display the arrow according to the action of toggle */}
                    {ticketState.currentDashboard?.settings?.groupBy
                        && ticketState.currentDashboard?.settings?.groupBy !== DashboardGroupBy.NoGroup
                        && (
                            <Button
                                variant="filter"
                                title="Collapse All Groups"
                                prefixIcon={<RiArrowUpDoubleFill />}
                                onClick={() => ticketState.dashboardView?.tryToggleAll()}
                                metricPath={[LoggingHandler.Metrics.AGENT, "controls", "collapse-all"]}
                            />
                        )}

                </div>
            )}
        </div>
    );
}
